import React from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight, FileCheck2, Landmark, ShieldCheck, Gavel, ScrollText, Banknote, Search,
    CheckCircle2, BadgeCheck, IndianRupee, HeartHandshake, Sparkles,
} from "lucide-react";
import PageShell from "@/components/PageShell";

const HERO_IMG = "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=2000&q=80";

const STEPS = [
    { icon: Search,       t: "Ownership Verification",     d: "We confirm the seller's title by tracing the ownership chain back at least 30 years through registered sale deeds and mother documents." },
    { icon: FileCheck2,   t: "Encumbrance Check",          d: "A fresh Encumbrance Certificate from the sub-registrar's office to ensure the property is free of loans, liens or third-party charges." },
    { icon: ShieldCheck,  t: "RERA Verification",          d: "Every project is cross-checked against the state RERA registry — registration validity, promoter history and complaint status." },
    { icon: Landmark,     t: "Land Title Verification",    d: "For land parcels — Revenue records (Khasra, Khatauni, 7/12 extract, Patta) are validated with the local Tehsil office." },
    { icon: ScrollText,   t: "Government Approvals",       d: "Layout plan, building sanction, occupancy certificate and completion certificate — verified from the competent authority." },
    { icon: Banknote,     t: "Bank Approval Status",       d: "We check whether the project is pre-approved by leading banks (SBI, HDFC, ICICI, Axis, Kotak) — an important secondary signal of legal soundness." },
    { icon: Gavel,        t: "Litigation Check",           d: "Court records and cause lists at civil, revenue and consumer forums are searched for any pending or historic litigation on the property or promoter." },
];

const OUTCOMES = [
    { icon: BadgeCheck,     l: "Legally Verified"  },
    { icon: ShieldCheck,    l: "Title Verified"    },
    { icon: CheckCircle2,   l: "RERA Verified"     },
    { icon: HeartHandshake, l: "Builder Authorized"},
    { icon: IndianRupee,    l: "Price Match"       },
    { icon: Sparkles,       l: "EBG Trust Score"   },
];

const FAQS = [
    { q: "Is the EBG Legal Check free of cost?",
      a: "Yes. Every property listed on EBG Realty is legally verified by our in-house counsel before it goes live. There is no charge to the buyer." },
    { q: "How long does the verification take?",
      a: "For a listed property, the check has already been completed. For a private off-market home you are considering, our team completes the 7-step process in 5–7 working days." },
    { q: "Does verification guarantee the property is safe?",
      a: "Our verification substantially reduces legal risk by surfacing every publicly-discoverable issue. It is not a warranty — the seller's disclosures remain the primary source of truth and we always recommend a joint sale-deed review by your personal counsel before registration." },
    { q: "Do you share the verification report with buyers?",
      a: "Yes. Once you commence a serious purchase enquiry with EBG, we share the full legal opinion including EC, RERA certificate, title chain memo and litigation search — under NDA." },
];

export default function LegalCheck() {
    return (
        <PageShell navVariant="dark">
            {/* HERO */}
            <section className="relative bg-[#0a0d18] text-white overflow-hidden" data-testid="legal-hero">
                <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30"/>
                <div className="absolute inset-0 bg-gradient-to-r from-[#0a0d18] via-[#0a0d18]/70 to-transparent"/>
                <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 pt-36 pb-20 md:pt-44 md:pb-24">
                    <div className="text-[10px] tracking-[0.28em] font-bold text-[var(--ebg-gold)] uppercase mb-5">EBG Legal Check</div>
                    <h1 className="font-serif text-4xl md:text-6xl lg:text-[72px] leading-[1.02] max-w-4xl">
                        Legally verified.<br/>
                        <em className="not-italic text-[var(--ebg-gold)]">Before you even see it.</em>
                    </h1>
                    <p className="text-white/70 max-w-2xl mt-6 leading-relaxed text-base md:text-lg">
                        Every residence on EBG Realty passes a rigorous 7-step legal verification by our in-house
                        counsel — so you buy with confidence, not on trust alone.
                    </p>
                    <div className="mt-10 flex flex-wrap gap-3">
                        {OUTCOMES.map((o, i) => (
                            <div key={i} className="border border-[var(--ebg-gold-line)] bg-black/40 backdrop-blur-md flex items-center gap-2 px-4 py-2.5" data-testid={`outcome-${i}`}>
                                <o.icon size={14} className="text-[var(--ebg-gold)]"/>
                                <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-white/90">{o.l}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 7 STEPS */}
            <section className="bg-[var(--ebg-ivory)]" data-testid="legal-steps">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-24">
                    <div className="grid lg:grid-cols-12 gap-8 mb-14">
                        <div className="lg:col-span-6">
                            <div className="text-[10px] tracking-[0.28em] font-bold text-[var(--ebg-gold)] uppercase">The 7-Step Verification</div>
                            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-[1.05]">
                                Independent. In-house.<br/>Uncompromising.
                            </h2>
                        </div>
                        <div className="lg:col-span-6 flex items-end">
                            <p className="text-black/60 leading-relaxed max-w-lg">
                                Our senior counsel has 20+ years combined experience across Bombay High Court,
                                Delhi NCR real-estate disputes and RERA authorities in five states.
                            </p>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-4 md:gap-5">
                        {STEPS.map((s, i) => (
                            <article
                                key={i}
                                data-testid={`legal-step-${i}`}
                                className="group flex gap-5 items-start bg-white border border-black/8 p-6 md:p-7 hover:border-[var(--ebg-gold-line)] hover:shadow-[0_20px_45px_-25px_rgba(0,0,0,0.25)] transition-all duration-500"
                            >
                                <div className="flex flex-col items-center shrink-0">
                                    <div className="w-14 h-14 rounded-full bg-[var(--ebg-gold)] text-black flex items-center justify-center">
                                        <s.icon size={20} strokeWidth={1.8}/>
                                    </div>
                                    <div className="display-num text-[var(--ebg-gold)] text-lg mt-3 tabular-nums">0{i + 1}</div>
                                </div>
                                <div>
                                    <h3 className="font-serif text-xl md:text-[22px] leading-tight">{s.t}</h3>
                                    <p className="text-black/60 text-sm mt-2.5 leading-relaxed">{s.d}</p>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* SAMPLE REPORT STRIP */}
            <section className="bg-[#0a0d18] text-white" data-testid="legal-report-strip">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-16 md:py-20 grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-6">
                        <div className="text-[10px] tracking-[0.28em] font-bold text-[var(--ebg-gold)] uppercase">A Sample Verification Memo</div>
                        <h2 className="font-serif text-4xl md:text-5xl mt-3 leading-[1.05]">
                            Transparent. Detailed.<br/>Handed to you upfront.
                        </h2>
                        <p className="text-white/60 mt-5 max-w-lg leading-relaxed text-[15px]">
                            You will receive the full memo — Encumbrance Certificate, RERA extract, title chain,
                            municipal approvals, litigation search results and our senior counsel's opinion — under NDA
                            once your purchase enquiry moves to shortlist stage.
                        </p>
                        <Link to="/contact" data-testid="legal-request-report" className="mt-8 inline-flex items-center gap-2 bg-[var(--ebg-gold)] text-black px-7 py-3.5 text-[11px] uppercase tracking-[0.28em] font-bold hover:bg-white transition-colors">
                            Request a Sample Report <ArrowRight size={12}/>
                        </Link>
                    </div>
                    <div className="lg:col-span-6">
                        <div className="border border-[var(--ebg-gold-line)] bg-black/40 backdrop-blur-md p-8">
                            <div className="flex items-center justify-between border-b border-white/10 pb-4 mb-5">
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--ebg-gold)]">Verification Memo</div>
                                    <div className="font-serif text-2xl mt-1">Skyline Residences · Worli, Mumbai</div>
                                </div>
                                <div className="w-14 h-14 rounded-full bg-[var(--ebg-gold)] text-black flex items-center justify-center font-bold text-lg">94</div>
                            </div>
                            <ul className="space-y-2.5 text-[13px]">
                                {[
                                    ["Title Chain", "Clean — 34 years traced"],
                                    ["Encumbrance", "Nil charges as on 12-Feb-2026"],
                                    ["RERA", "P51900019876 — Active"],
                                    ["Approvals", "OC + CC issued 08-Aug-2023"],
                                    ["Bank Approval", "SBI · HDFC · ICICI · Axis"],
                                    ["Litigation", "No pending case at any forum"],
                                    ["Counsel's Opinion", "Safe for purchase"],
                                ].map(([k, v]) => (
                                    <li key={k} className="flex items-start justify-between gap-3 border-b border-white/8 pb-2.5 last:border-b-0">
                                        <span className="text-white/60">{k}</span>
                                        <span className="text-white text-right">{v}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="bg-white border-t border-black/10" data-testid="legal-faq">
                <div className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-24">
                    <div className="text-[10px] tracking-[0.28em] font-bold text-[var(--ebg-gold)] uppercase text-center">Frequently Asked</div>
                    <h2 className="font-serif text-3xl md:text-4xl mt-3 text-center leading-tight">You have questions.<br/>We have straight answers.</h2>
                    <div className="mt-10 divide-y divide-black/10 border-y border-black/10">
                        {FAQS.map((f, i) => (
                            <details key={i} className="group py-6" data-testid={`legal-faq-${i}`}>
                                <summary className="flex items-start justify-between gap-6 cursor-pointer list-none">
                                    <span className="font-serif text-lg md:text-xl leading-snug">{f.q}</span>
                                    <span className="w-9 h-9 rounded-full border border-black/20 flex items-center justify-center shrink-0 group-open:bg-[var(--ebg-gold)] group-open:border-[var(--ebg-gold)] group-open:rotate-45 transition-all">
                                        <span className="text-lg leading-none">+</span>
                                    </span>
                                </summary>
                                <p className="text-black/65 leading-relaxed mt-4 text-[15px]">{f.a}</p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-[var(--ebg-ivory)] border-t border-black/10" data-testid="legal-cta">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-16 md:py-20 text-center">
                    <h3 className="font-serif text-3xl md:text-4xl leading-tight">Buy with confidence, not on trust alone.</h3>
                    <p className="text-black/60 mt-4 max-w-xl mx-auto">Every EBG residence carries a full legal opinion in the box. Explore our verified portfolio today.</p>
                    <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
                        <Link to="/properties" data-testid="legal-view-portfolio" className="bg-black text-white px-8 py-3.5 text-[11px] uppercase tracking-[0.28em] font-bold hover:bg-[var(--ebg-gold)] hover:text-black transition-colors inline-flex items-center gap-2">
                            View Verified Portfolio <ArrowRight size={12}/>
                        </Link>
                        <Link to="/contact" data-testid="legal-book-consult" className="border border-black text-black px-8 py-3.5 text-[11px] uppercase tracking-[0.28em] font-bold hover:bg-black hover:text-white transition-colors">
                            Book Legal Consultation
                        </Link>
                    </div>
                </div>
            </section>
        </PageShell>
    );
}
